import React, { useEffect, useState, useRef, createElement } from 'react';
import { SparklesIcon, SendIcon, Loader2Icon, ChevronDownIcon, ChevronUpIcon, Trash2Icon, WandSparklesIcon, ImagePlusIcon, XIcon, PaperclipIcon, FileTextIcon, CheckCircle2Icon, ImageIcon } from 'lucide-react';
import { callGPTAPI, uploadImageToHost } from '../utils/api';
import { mediaLibraryApi, MediaLibraryRecord } from '../services/creatorApi';
interface GptCodeAssistantProps {
  mode: 'form' | 'email';
  currentCode: {
    html: string;
    css?: string;
    js?: string;
  };
  onCodeGenerated: (code: {
    html: string;
    css?: string;
    js?: string;
  }) => void;
}
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  codeApplied?: boolean;
  timestamp: Date;
}
interface AttachedImage {
  id: string;
  name: string;
  dataUrl: string;
}
interface AttachedFile {
  id: string;
  name: string;
  dataUrl: string;
  size: number;
  type: string;
}
const ACCEPTED_FILE_TYPES = ['application/pdf', 'text/plain', 'text/csv', 'text/html', 'text/css', 'text/markdown', 'application/json', 'application/xml', 'text/xml', 'text/javascript', 'application/javascript', 'image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml', 'image/bmp', 'image/tiff'];
const ACCEPTED_FILE_EXTENSIONS = '.pdf,.txt,.csv,.json,.md,.html,.css,.js,.xml,.tsx,.ts,.png,.jpg,.jpeg,.gif,.webp,.svg,.bmp,.tiff';
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
/** Compress an image dataUrl by resizing and reducing quality to keep GPT payloads small */
function compressImage(dataUrl: string, maxDim = 800, quality = 0.65): Promise<string> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      let w = img.width;
      let h = img.height;
      // Scale down if larger than maxDim
      if (w > maxDim || h > maxDim) {
        if (w > h) {
          h = Math.round(h * (maxDim / w));
          w = maxDim;
        } else {
          w = Math.round(w * (maxDim / h));
          h = maxDim;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(dataUrl); // fallback to original
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      const compressed = canvas.toDataURL('image/jpeg', quality);
      // Only use compressed version if it's actually smaller
      resolve(compressed.length < dataUrl.length ? compressed : dataUrl);
    };
    img.onerror = () => resolve(dataUrl); // fallback to original
    img.src = dataUrl;
  });
}
const FORM_INSTRUCTIONS = `You are an expert frontend developer specializing in HTML signup forms. Your job is to generate clean, professional, production-ready signup form code.

RULES:
1. Return code in EXACTLY this format with three fenced code blocks labeled html, css, and js:
\`\`\`html
(your HTML here)
\`\`\`
\`\`\`css
(your CSS here)
\`\`\`
\`\`\`js
(your JavaScript here, if needed)
\`\`\`
2. The HTML block must contain ONLY the form markup — just the <form> element and its contents. Do NOT include <!DOCTYPE>, <html>, <head>, <body>, <link>, or <style> tags. Those are handled separately.
3. The HTML must contain a <form> element with proper input fields using name attributes
4. Common field names: email, first_name, last_name, phone, company, message
5. Always include a submit button with type="submit"
6. The CSS block contains ALL the styling. Use class names that match the classes in your HTML. The CSS will be injected into a <style> tag automatically.
7. Use modern, clean styling. Make it look professional and polished
8. The JS block can be empty if no custom behavior is needed — still include the empty block
9. Forms should be responsive and mobile-friendly
10. Include proper placeholder text and labels for accessibility
11. If the user asks for adjustments, you MUST return the COMPLETE updated code (all three blocks) with the requested changes applied. Do NOT return the same code unchanged.
12. NEVER put CSS inside the HTML block. ALL styles go in the CSS block.
13. NEVER include <style> tags in the HTML block — put all CSS in the css block.
14. If the user asks a QUESTION (not a code request), respond with a helpful text answer. You may include code blocks if relevant, but you can also answer with plain text when appropriate.
15. You can include brief explanations BEFORE or AFTER the code blocks to explain what you changed or answer questions. Keep explanations concise.`;
const EMAIL_INSTRUCTIONS = `You are an expert email HTML developer. Your job is to generate email-compatible HTML that renders correctly across all major email clients (Gmail, Outlook, Apple Mail, Yahoo).

RULES:
1. Return code in EXACTLY this format with one fenced code block labeled html:
\`\`\`html
(your complete email HTML here)
\`\`\`
2. Use TABLE-BASED layouts — email clients don't support flexbox/grid
3. Use inline styles — email clients strip <style> blocks inconsistently
4. Set a max-width container (typically 600px) centered with margin: 0 auto
5. Use web-safe fonts with fallbacks: Arial, Helvetica, sans-serif
6. Include proper DOCTYPE and html/head/body structure
7. All images must use absolute URLs and have alt text
8. Use role="presentation" on layout tables
9. Include cellpadding="0" cellspacing="0" on all tables
10. Support the merge variable {{confirmation_link}} for the confirmation button/link
11. Support {{team_name}} for the team/brand name
12. Support any form field variables like {{first_name}}, {{email}}, etc.
13. Make the design professional, clean, and visually appealing
14. If the user asks for adjustments, you MUST return the COMPLETE updated HTML with the requested changes applied. Do NOT return the same code unchanged.
15. If the user asks a QUESTION (not a code request), respond with a helpful text answer. You may include code blocks if relevant, but you can also answer with plain text when appropriate.
16. You can include brief explanations BEFORE or AFTER the code blocks to explain what you changed or answer questions. Keep explanations concise.`;
function parseCodeBlocks(response: string): {
  html: string;
  css?: string;
  js?: string;
} {
  const result: {
    html: string;
    css?: string;
    js?: string;
  } = {
    html: ''
  };
  // Extract HTML block
  const htmlMatch = response.match(/```html\s*\n([\s\S]*?)```/i);
  if (htmlMatch) {
    result.html = htmlMatch[1].trim();
  }
  // Extract CSS block
  const cssMatch = response.match(/```css\s*\n([\s\S]*?)```/i);
  if (cssMatch) {
    result.css = cssMatch[1].trim();
  }
  // Extract JS/JavaScript block
  const jsMatch = response.match(/```(?:js|javascript)\s*\n([\s\S]*?)```/i);
  if (jsMatch) {
    result.js = jsMatch[1].trim();
  }
  // Fallback: if no code blocks found, treat entire response as HTML
  if (!result.html && !result.css && !result.js) {
    // Try to find any generic code block
    const genericMatch = response.match(/```\s*\n([\s\S]*?)```/i);
    if (genericMatch) {
      result.html = genericMatch[1].trim();
    } else {
      result.html = response.trim();
    }
  }
  return result;
}
/** Check if a GPT response contains any code blocks */
function hasCodeBlocks(response: string): boolean {
  return /```(?:html|css|js|javascript)\s*\n/i.test(response);
}
/** Extract explanation text from a GPT response (everything outside code blocks) */
function extractExplanation(response: string): string {
  // Remove all fenced code blocks and their content
  const withoutCode = response.replace(/```(?:html|css|js|javascript)\s*\n[\s\S]*?```/gi, '').trim();
  // Clean up excessive whitespace
  return withoutCode.replace(/\n{3,}/g, '\n\n').trim();
}
function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 6);
}
export function GptCodeAssistant({
  mode,
  currentCode,
  onCodeGenerated
}: GptCodeAssistantProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [errorDebug, setErrorDebug] = useState<Record<string, any> | null>(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugCopied, setDebugCopied] = useState(false);
  const [attachedImages, setAttachedImages] = useState<AttachedImage[]>([]);
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  // Media Library images — fetched when assistant expands
  const [mediaImages, setMediaImages] = useState<MediaLibraryRecord[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaPickerSearch, setMediaPickerSearch] = useState('');
  const [selectedMediaIds, setSelectedMediaIds] = useState<Set<string>>(new Set());
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const mediaPickerRef = useRef<HTMLDivElement>(null);
  // Store the last request payload so we can retry on failure
  const lastRequestRef = useRef<{
    instructions: string;
    promptPayload: any;
  } | null>(null);
  // Use a ref to always have the latest currentCode, avoiding stale closures
  const currentCodeRef = useRef(currentCode);
  useEffect(() => {
    currentCodeRef.current = currentCode;
  }, [currentCode]);
  // Fetch media library images when assistant is expanded
  useEffect(() => {
    if (isExpanded && mediaImages.length === 0 && !isLoadingMedia) {
      setIsLoadingMedia(true);
      mediaLibraryApi.list().then(images => setMediaImages(images)).catch(err => console.warn('Failed to load media library:', err)).finally(() => setIsLoadingMedia(false));
    }
  }, [isExpanded]);
  useEffect(() => {
    if (messagesEndRef.current && messages.length > 0) {
      messagesEndRef.current.scrollIntoView({
        behavior: 'smooth'
      });
    }
  }, [messages]);
  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [prompt]);
  // Close media picker when clicking outside
  useEffect(() => {
    if (!showMediaPicker) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (mediaPickerRef.current && !mediaPickerRef.current.contains(e.target as Node)) {
        setShowMediaPicker(false);
        setMediaPickerSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMediaPicker]);
  // Insert a media image reference into the prompt
  const handleInsertMediaImage = (img: MediaLibraryRecord) => {
    const insertion = `Use the image "${img.Image_Name}" `;
    setPrompt(prev => {
      const trimmed = prev.trim();
      if (trimmed) {
        return trimmed + (trimmed.endsWith('.') || trimmed.endsWith(',') ? ' ' : '. ') + insertion;
      }
      return insertion;
    });
    // Track this image so buildPrompt includes its full URL
    setSelectedMediaIds(prev => new Set(prev).add(img.ID));
    setShowMediaPicker(false);
    setMediaPickerSearch('');
    setTimeout(() => textareaRef.current?.focus(), 50);
  };
  const filteredMediaImages = mediaImages.filter(img => {
    if (!mediaPickerSearch) return true;
    const q = mediaPickerSearch.toLowerCase();
    return img.Image_Name.toLowerCase().includes(q) || (img.Category || '').toLowerCase().includes(q) || (img.Description || '').toLowerCase().includes(q);
  });
  // Store base64 URLs stripped from editor code so we can restore them after GPT responds
  const strippedBase64Ref = useRef<Map<string, string>>(new Map());
  // Cache hosted URLs for media images that were originally base64
  const hostedUrlCacheRef = useRef<Map<string, string>>(new Map());
  // Strip base64 data URLs from code to keep GPT payload small
  // Returns cleaned code with placeholders, and stores the originals for restoration
  const stripBase64FromCode = (code: string): string => {
    const map = new Map<string, string>();
    let counter = 0;
    const cleaned = code.replace(/data:image\/[^"'\s)}\]]+/g, match => {
      // Check if it matches a known media library image
      const knownImg = mediaImages.find(m => m.Image_URL === match);
      if (knownImg) {
        return `{{MEDIA_IMAGE:${knownImg.Image_Name}}}`;
      }
      // Unknown base64 — use numbered placeholder
      counter++;
      const placeholder = `{{BASE64_IMAGE_${counter}}}`;
      map.set(placeholder, match);
      return placeholder;
    });
    strippedBase64Ref.current = map;
    return cleaned;
  };
  // Replace all placeholders in GPT output with actual URLs
  // For base64 media images, attempt to upload them to get hosted URLs
  const replaceMediaPlaceholders = async (text: string): Promise<string> => {
    let result = text;
    // First pass: collect all media image placeholders that need uploading
    const mediaMatches: {
      placeholder: string;
      name: string;
    }[] = [];
    result.replace(/\{\{MEDIA_IMAGE:([^}]+)\}\}/g, (match, name) => {
      mediaMatches.push({
        placeholder: match,
        name: name.trim()
      });
      return match;
    });
    // For each media image, resolve to a hosted URL
    for (const {
      placeholder,
      name
    } of mediaMatches) {
      const img = mediaImages.find(m => m.Image_Name === name || m.Image_Name.trim() === name);
      if (!img) continue;
      let url = img.Image_URL;
      // If it's a base64 data URL, try to get/upload a hosted version
      if (url?.startsWith('data:')) {
        // Check cache first
        const cached = hostedUrlCacheRef.current.get(img.ID);
        if (cached) {
          url = cached;
        } else {
          try {
            const hostedUrl = await uploadImageToHost(url, img.Image_Name);
            hostedUrlCacheRef.current.set(img.ID, hostedUrl);
            url = hostedUrl;
          } catch {
            // Upload failed — fall back to data URL (will work for display but not in emails)
            console.warn(`Failed to host media image "${img.Image_Name}", using data URL fallback`);
          }
        }
      }
      result = result.split(placeholder).join(url || placeholder);
    }
    // Replace {{BASE64_IMAGE_N}} with stored base64 URLs
    result = result.replace(/\{\{BASE64_IMAGE_\d+\}\}/g, match => {
      return strippedBase64Ref.current.get(match) || match;
    });
    return result;
  };
  const buildPrompt = (userMessage: string): string => {
    // Use ref to get the absolute latest code from the editor
    const latestCode = currentCodeRef.current;
    // For follow-up prompts, include only the user's previous requests (not raw GPT responses)
    // This avoids confusing GPT with old code blocks in conversation history
    let conversationContext = '';
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length > 0) {
      conversationContext = '\n\nPREVIOUS REQUESTS FROM USER:\n';
      for (const msg of userMessages.slice(-4)) {
        conversationContext += `- ${msg.content}\n`;
      }
      conversationContext += '\nThe code below already reflects those previous requests. Now apply the NEW request.\n';
    }
    // Always include the current code from the editor so GPT modifies the latest version
    // Strip base64 data URLs to keep payload small — they'll be restored after GPT responds
    let codeContext = '';
    const hasExistingCode = latestCode.html.trim() || latestCode.css?.trim() || latestCode.js?.trim();
    if (hasExistingCode) {
      codeContext = '\n\nCURRENT CODE IN THE EDITOR (modify this code according to the user request below):\n';
      if (latestCode.html.trim()) {
        codeContext += '```html\n' + stripBase64FromCode(latestCode.html) + '\n```\n';
      }
      if (mode === 'form') {
        if (latestCode.css?.trim()) {
          codeContext += '```css\n' + stripBase64FromCode(latestCode.css) + '\n```\n';
        }
        if (latestCode.js?.trim()) {
          codeContext += '```js\n' + stripBase64FromCode(latestCode.js) + '\n```\n';
        }
      }
    }
    // Inject available media library images so GPT can use them
    // For base64 images: use a placeholder token that we'll replace AFTER GPT responds (to keep payload small)
    // For regular URLs: include them directly (they're short)
    let mediaContext = '';
    if (mediaImages.length > 0) {
      mediaContext = '\n\nAVAILABLE IMAGES FROM MEDIA LIBRARY (use these when the user references an image by name):\n';
      for (const img of mediaImages) {
        const isBase64 = img.Image_URL?.startsWith('data:');
        if (isBase64) {
          // Use placeholder — actual URL will be substituted after GPT responds
          mediaContext += `- "${img.Image_Name}": USE EXACTLY THIS as the src value → {{MEDIA_IMAGE:${img.Image_Name}}}`;
        } else {
          mediaContext += `- "${img.Image_Name}": ${img.Image_URL}`;
        }
        if (img.Category) mediaContext += ` [${img.Category}]`;
        if (img.Description) mediaContext += ` — ${img.Description}`;
        mediaContext += '\n';
      }
      mediaContext += 'When the user mentions an image name, use its URL (or placeholder token) as the src. Always include alt text.\n' + 'IMPORTANT: If the current code contains {{MEDIA_IMAGE:...}} or {{BASE64_IMAGE_...}} placeholders, preserve them exactly as-is in your output — they will be replaced with actual image data automatically.\n';
    }
    return `${conversationContext}${codeContext}${mediaContext}\nNEW USER REQUEST (apply these changes to the code above): ${userMessage}`;
  };
  const processImageFiles = (files: File[]) => {
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    const remaining = 3 - attachedImages.length;
    const toProcess = imageFiles.slice(0, remaining);
    toProcess.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => {
        const dataUrl = ev.target?.result as string;
        setAttachedImages(prev => [...prev, {
          id: generateId(),
          name: file.name,
          dataUrl
        }]);
      };
      reader.readAsDataURL(file);
    });
  };
  const processDocumentFiles = (files: File[]) => {
    const docFiles = files.filter(f => ACCEPTED_FILE_TYPES.includes(f.type) || /\.(pdf|txt|csv|json|md|html|css|js|xml|tsx|ts)$/i.test(f.name));
    if (!docFiles.length) return;
    const remaining = 3 - attachedFiles.length;
    const toProcess = docFiles.slice(0, remaining);
    toProcess.forEach(file => {
      const reader = new FileReader();
      reader.onload = ev => {
        const dataUrl = ev.target?.result as string;
        setAttachedFiles(prev => [...prev, {
          id: generateId(),
          name: file.name,
          dataUrl,
          size: file.size,
          type: file.type
        }]);
      };
      reader.readAsDataURL(file);
    });
  };
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    processImageFiles(files);
    e.target.value = '';
  };
  const handleDocSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    // Route images to the image handler, everything else to document handler
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const docFiles = files.filter(f => !f.type.startsWith('image/'));
    if (imageFiles.length) processImageFiles(imageFiles);
    if (docFiles.length) processDocumentFiles(docFiles);
    e.target.value = '';
  };
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragOver(true);
    }
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragOver(false);
    }
  };
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    // Separate images from documents
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const docFiles = files.filter(f => !f.type.startsWith('image/'));
    if (imageFiles.length) processImageFiles(imageFiles);
    if (docFiles.length) processDocumentFiles(docFiles);
  };
  // Paste image from clipboard (e.g. Ctrl+V screenshot)
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter(item => item.type.startsWith('image/'));
    if (!imageItems.length) return;
    e.preventDefault();
    imageItems.forEach(item => {
      const file = item.getAsFile();
      if (file) processImageFiles([file]);
    });
  };
  const removeImage = (id: string) => {
    setAttachedImages(prev => prev.filter(img => img.id !== id));
  };
  const removeFile = (id: string) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== id));
  };
  const handleSubmit = async () => {
    const trimmedPrompt = prompt.trim();
    if (!trimmedPrompt && attachedImages.length === 0 && attachedFiles.length === 0 || isLoading) return;
    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: trimmedPrompt || (attachedFiles.length > 0 ? `(${attachedFiles.length} file${attachedFiles.length > 1 ? 's' : ''} attached)` : '(image attached)'),
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setPrompt('');
    setError(null);
    setIsLoading(true);
    const imagesToSend = [...attachedImages];
    const filesToSend = [...attachedFiles];
    setAttachedImages([]);
    setAttachedFiles([]);
    setSelectedMediaIds(new Set());
    try {
      const instructions = mode === 'form' ? FORM_INSTRUCTIONS : EMAIL_INSTRUCTIONS;
      const fullPromptText = buildPrompt(trimmedPrompt || '(see attached files — use them as context for code generation)');
      // Compress attached images aggressively to keep payload under server limits (~450KB)
      const compressedImages = await Promise.all(imagesToSend.map(async img => ({
        ...img,
        dataUrl: await compressImage(img.dataUrl, 600, 0.5)
      })));
      // Format prompt for OpenAI Responses API content types
      const hasAttachments = compressedImages.length > 0 || filesToSend.length > 0;
      const promptPayload: any = !hasAttachments ? fullPromptText : [{
        type: 'input_text',
        text: fullPromptText
      }, ...compressedImages.map(img => ({
        type: 'input_image',
        image_url: img.dataUrl
      })), ...filesToSend.map(file => ({
        type: 'input_file',
        filename: file.name,
        file_data: file.dataUrl
      }))];
      // Save request for retry
      lastRequestRef.current = {
        instructions,
        promptPayload
      };
      const rawResult = await callGPTAPI(instructions, promptPayload);
      // Replace media placeholders with actual URLs before processing
      // For base64 images, this will attempt to upload them to get hosted URLs
      const result = await replaceMediaPlaceholders(rawResult);
      // Check if the response contains code blocks
      const containsCode = hasCodeBlocks(result);
      const explanation = extractExplanation(result);
      if (containsCode) {
        // Apply code to editor
        const parsedCode = parseCodeBlocks(result);
        onCodeGenerated(parsedCode);
        // Show explanation + code applied indicator
        const assistantMessage: Message = {
          id: generateId(),
          role: 'assistant',
          content: explanation || 'Code updated and applied to editor.',
          codeApplied: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        // Pure text response (answering a question)
        const assistantMessage: Message = {
          id: generateId(),
          role: 'assistant',
          content: result.trim(),
          codeApplied: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate code';
      setError(errorMessage);
      setErrorDebug((err as any)?.debug || null);
      setShowDebug(false);
      setDebugCopied(false);
    } finally {
      setIsLoading(false);
    }
  };
  const handleRetry = async () => {
    if (!lastRequestRef.current || isLoading) return;
    setError(null);
    setErrorDebug(null);
    setShowDebug(false);
    setIsLoading(true);
    try {
      const {
        instructions,
        promptPayload
      } = lastRequestRef.current;
      const rawResult = await callGPTAPI(instructions, promptPayload);
      const result = await replaceMediaPlaceholders(rawResult);
      const containsCode = hasCodeBlocks(result);
      const explanation = extractExplanation(result);
      if (containsCode) {
        const parsedCode = parseCodeBlocks(result);
        onCodeGenerated(parsedCode);
        const assistantMessage: Message = {
          id: generateId(),
          role: 'assistant',
          content: explanation || 'Code updated and applied to editor.',
          codeApplied: true,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      } else {
        const assistantMessage: Message = {
          id: generateId(),
          role: 'assistant',
          content: result.trim(),
          codeApplied: false,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, assistantMessage]);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate code';
      setError(errorMessage);
      setErrorDebug((err as any)?.debug || null);
      setShowDebug(false);
      setDebugCopied(false);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };
  const handleClearHistory = () => {
    setMessages([]);
    setError(null);
    setErrorDebug(null);
    setShowDebug(false);
  };
  const modeLabel = mode === 'form' ? 'Signup Form' : 'Email Template';
  return <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden" data-id="element-1936">
      {/* Toggle Header */}
      <button onClick={() => setIsExpanded(!isExpanded)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors" data-id="element-1937">
        <div className="flex items-center gap-2.5" data-id="element-1938">
          <div className="flex items-center justify-center w-7 h-7 rounded-md bg-purple-100" data-id="element-1939">
            <WandSparklesIcon className="h-4 w-4 text-purple-600" data-id="element-1940" />
          </div>
          <div data-id="element-1941">
            <span className="text-sm font-medium text-gray-900" data-id="element-1942">
              AI Code Assistant
            </span>
            <span className="text-xs text-gray-500 ml-2" data-id="element-1943">
              Generate {modeLabel} code with GPT
            </span>
          </div>
        </div>
        {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-gray-400" data-id="element-1944" /> : <ChevronDownIcon className="h-4 w-4 text-gray-400" data-id="element-1945" />}
      </button>

      {/* Expanded Content */}
      {isExpanded && <div className="border-t border-gray-200 relative" onDragEnter={handleDragEnter} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={handleDrop} data-id="element-1946">
          {/* Drag-over overlay */}
          {isDragOver && <div className="absolute inset-0 z-10 bg-purple-50/90 border-2 border-dashed border-purple-400 rounded-b-lg flex flex-col items-center justify-center gap-2 pointer-events-none" data-id="element-1947">
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center" data-id="element-1948">
                <PaperclipIcon className="h-6 w-6 text-purple-600" data-id="element-1949" />
              </div>
              <p className="text-sm font-medium text-purple-700" data-id="element-1950">
                Drop files here
              </p>
              <p className="text-xs text-purple-500" data-id="element-1951">
                Images, PDFs, text files, and more
              </p>
            </div>}

          {/* Messages Area */}
          {messages.length > 0 && <div className="max-h-80 overflow-y-auto px-4 py-3 space-y-3 bg-gray-50/50" data-id="element-1952">
              {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-1953">
                  <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${msg.role === 'user' ? 'bg-gray-900 text-white' : 'bg-white border border-gray-200 text-gray-700'}`} data-id="element-1954">
                    {msg.role === 'assistant' ? <div className="space-y-1.5" data-id="element-1955">
                        {msg.codeApplied && <div className="flex items-center gap-1.5 pb-1 mb-1.5 border-b border-gray-100" data-id="element-1956">
                            <CheckCircle2Icon className="h-3.5 w-3.5 text-green-600 flex-shrink-0" data-id="element-1957" />
                            <span className="text-xs text-green-700 font-medium" data-id="element-1958">
                              Code applied to editor
                            </span>
                          </div>}
                        <div className="flex items-start gap-2" data-id="element-1959">
                          <SparklesIcon className="h-3.5 w-3.5 text-purple-500 mt-0.5 flex-shrink-0" data-id="element-1960" />
                          <p className="whitespace-pre-wrap text-sm text-gray-700 leading-relaxed" data-id="element-1961">
                            {msg.content}
                          </p>
                        </div>
                      </div> : <p className="whitespace-pre-wrap" data-id="element-1962">{msg.content}</p>}
                  </div>
                </div>)}
              {isLoading && <div className="flex justify-start" data-id="element-1963">
                  <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 flex items-center gap-2" data-id="element-1964">
                    <Loader2Icon className="h-3.5 w-3.5 text-purple-500 animate-spin" data-id="element-1965" />
                    <span className="text-xs text-gray-500" data-id="element-1966">Thinking...</span>
                  </div>
                </div>}
              <div ref={messagesEndRef} data-id="element-1967" />
            </div>}

          {/* Error */}
          {error && <div className="mx-4 mt-3 bg-red-50 border border-red-200 rounded-md p-3 space-y-2" data-id="element-1968">
              <div className="flex items-start justify-between gap-3" data-id="element-1969">
                <p className="text-xs text-red-700 flex-1" data-id="element-1970">{error}</p>
                <button onClick={handleRetry} disabled={isLoading} className="flex-shrink-0 px-2.5 py-1 text-xs font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50" data-id="element-1971">
                  Retry
                </button>
              </div>
              {errorDebug && <div className="border-t border-red-200 pt-2" data-id="element-1972">
                  <button onClick={() => setShowDebug(v => !v)} className="text-[11px] text-red-600 hover:text-red-800 underline underline-offset-2" data-id="element-1973">
                    {showDebug ? 'Hide' : 'Show'} debug details
                  </button>
                  {showDebug && <div className="mt-2 space-y-1.5" data-id="element-1974">
                      <pre className="text-[10px] text-red-800 bg-red-100/60 rounded p-2 overflow-x-auto max-h-48 overflow-y-auto whitespace-pre-wrap break-all font-mono leading-relaxed" data-id="element-1975">
                        {JSON.stringify(errorDebug, null, 2)}
                      </pre>
                      <button onClick={() => {
              const debugText = `GPT API Error Report\n${'='.repeat(40)}\nError: ${error}\n\nDebug Details:\n${JSON.stringify(errorDebug, null, 2)}`;
              navigator.clipboard.writeText(debugText).then(() => {
                setDebugCopied(true);
                setTimeout(() => setDebugCopied(false), 2000);
              });
            }} className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors" data-id="element-1976">
                        {debugCopied ? <>
                            <CheckCircle2Icon className="h-3 w-3" data-id="element-1977" />
                            Copied!
                          </> : 'Copy debug info'}
                      </button>
                    </div>}
                </div>}
            </div>}

          {/* Input Area */}
          <div className="p-4 space-y-3" data-id="element-1978">
            {messages.length === 0 && <div className="space-y-2" data-id="element-1979">
                <p className="text-xs text-gray-500" data-id="element-1980">
                  Describe what you want and GPT will generate the code. Attach
                  screenshots, PDFs, or other files by dropping them here,
                  pasting (Ctrl+V), or using the buttons below. Refine with
                  follow-up messages.
                </p>
                {/* Quick suggestions */}
                <div className="flex flex-wrap gap-1.5" data-id="element-1981">
                  {mode === 'form' ? <>
                      <button onClick={() => setPrompt('Create a clean newsletter signup form with email, first name, and last name fields')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-1982">
                        Newsletter signup
                      </button>
                      <button onClick={() => setPrompt('Create a contact form with name, email, phone, and message fields with a modern dark theme')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-1983">
                        Contact form
                      </button>
                      <button onClick={() => setPrompt('Create a minimal email-only subscription form with inline button')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-1984">
                        Minimal subscribe
                      </button>
                    </> : <>
                      <button onClick={() => setPrompt('Create a professional confirmation email with a prominent confirm button using {{confirmation_link}}, greeting with {{first_name}}, and {{team_name}} branding')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-1985">
                        Confirmation email
                      </button>
                      <button onClick={() => setPrompt('Create a welcome email template with a hero image area, welcome message for {{first_name}}, and a CTA button')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-1986">
                        Welcome email
                      </button>
                      <button onClick={() => setPrompt('Create a minimal, text-focused confirmation email with {{confirmation_link}} and {{team_name}}')} className="px-2.5 py-1 text-xs text-gray-600 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors" data-id="element-1987">
                        Minimal confirmation
                      </button>
                    </>}
                </div>
              </div>}

            {/* Attached image thumbnails */}
            {attachedImages.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-1988">
                {attachedImages.map(img => <div key={img.id} className="relative group" data-id="element-1989">
                    <img src={img.dataUrl} alt={img.name} className="h-16 w-16 object-cover rounded-lg border border-gray-200" data-id="element-1990" />
                    <button onClick={() => removeImage(img.id)} className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-gray-800 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Remove image" data-id="element-1991">
                      <XIcon className="h-2.5 w-2.5" data-id="element-1992" />
                    </button>
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 rounded-b-lg px-1 py-0.5" data-id="element-1993">
                      <p className="text-[9px] text-white truncate" data-id="element-1994">
                        {img.name}
                      </p>
                    </div>
                  </div>)}
                {attachedImages.length < 3 && <button onClick={() => fileInputRef.current?.click()} className="h-16 w-16 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center text-gray-400 hover:border-purple-400 hover:text-purple-500 transition-colors" title="Add another image" data-id="element-1995">
                    <ImagePlusIcon className="h-5 w-5" data-id="element-1996" />
                  </button>}
              </div>}

            {/* Attached file chips */}
            {attachedFiles.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-1997">
                {attachedFiles.map(file => <div key={file.id} className="group flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-1998">
                    <FileTextIcon className="h-4 w-4 text-purple-500 flex-shrink-0" data-id="element-1999" />
                    <div className="min-w-0" data-id="element-2000">
                      <p className="text-xs font-medium text-gray-700 truncate max-w-[140px]" data-id="element-2001">
                        {file.name}
                      </p>
                      <p className="text-[10px] text-gray-400" data-id="element-2002">
                        {formatFileSize(file.size)}
                      </p>
                    </div>
                    <button onClick={() => removeFile(file.id)} className="ml-1 p-0.5 text-gray-400 hover:text-red-500 rounded transition-colors" title="Remove file" data-id="element-2003">
                      <XIcon className="h-3 w-3" data-id="element-2004" />
                    </button>
                  </div>)}
                {attachedFiles.length < 3 && <button onClick={() => docInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 border-2 border-dashed border-gray-300 rounded-lg text-gray-400 hover:border-purple-400 hover:text-purple-500 transition-colors text-xs" title="Add another file" data-id="element-2005">
                    <PaperclipIcon className="h-3.5 w-3.5" data-id="element-2006" />
                    Add file
                  </button>}
              </div>}

            {/* Hidden file inputs */}
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleImageSelect} data-id="element-2007" />
            <input ref={docInputRef} type="file" accept={ACCEPTED_FILE_EXTENSIONS} multiple className="hidden" onChange={handleDocSelect} data-id="element-2008" />

            <div className="flex items-end gap-2" data-id="element-2009">
              <div className="flex-1 relative" data-id="element-2010">
                <textarea ref={textareaRef} value={prompt} onChange={e => setPrompt(e.target.value)} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={messages.length === 0 ? mode === 'form' ? 'Describe the signup form you want...' : 'Describe the email template you want...' : 'Describe adjustments...'} rows={1} disabled={isLoading} className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-purple-500 focus:ring-purple-500 text-sm resize-none pr-3 py-2.5 disabled:opacity-50 disabled:bg-gray-50" data-id="element-2011" />
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-2012">
                {/* File upload button */}
                <button onClick={() => docInputRef.current?.click()} disabled={isLoading || attachedFiles.length >= 3} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title={attachedFiles.length >= 3 ? 'Max 3 files' : 'Attach file (images, PDF, TXT, CSV...)'} data-id="element-2013">
                  <PaperclipIcon className="h-4 w-4" data-id="element-2014" />
                </button>
                {/* Image upload button */}
                <button onClick={() => fileInputRef.current?.click()} disabled={isLoading || attachedImages.length >= 3} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed" title={attachedImages.length >= 3 ? 'Max 3 images' : 'Attach screenshot'} data-id="element-2015">
                  <ImagePlusIcon className="h-4 w-4" data-id="element-2016" />
                </button>
                {messages.length > 0 && <button onClick={handleClearHistory} disabled={isLoading} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50" title="Clear conversation" data-id="element-2017">
                    <Trash2Icon className="h-4 w-4" data-id="element-2018" />
                  </button>}
                <button onClick={handleSubmit} disabled={isLoading || !prompt.trim() && attachedImages.length === 0 && attachedFiles.length === 0} className="p-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" title="Generate code" data-id="element-2019">
                  {isLoading ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-2020" /> : <SendIcon className="h-4 w-4" data-id="element-2021" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between" data-id="element-2022">
              <p className="text-[11px] text-gray-400" data-id="element-2023">
                Enter to send · Shift+Enter for new line · Drop or paste
                (Ctrl+V) images · Attach images, PDFs, text files & more
              </p>
              {mediaImages.length > 0 && <div className="relative" ref={mediaPickerRef} data-id="element-2024">
                  <button type="button" onClick={() => {
              setShowMediaPicker(prev => !prev);
              setMediaPickerSearch('');
            }} className="inline-flex items-center gap-1 text-[11px] text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full flex-shrink-0 hover:bg-emerald-100 transition-colors cursor-pointer" data-id="element-2025">
                    <ImageIcon className="h-3 w-3" data-id="element-2026" />
                    {mediaImages.length} image
                    {mediaImages.length !== 1 ? 's' : ''} available
                    <ChevronUpIcon className={`h-3 w-3 transition-transform ${showMediaPicker ? '' : 'rotate-180'}`} data-id="element-2027" />
                  </button>

                  {/* Media Picker Dropdown */}
                  {showMediaPicker && <div className="absolute bottom-full right-0 mb-2 w-80 bg-white border border-gray-200 rounded-lg shadow-xl z-20 overflow-hidden" data-id="element-2028">
                      <div className="px-3 py-2.5 border-b border-gray-100" data-id="element-2029">
                        <div className="flex items-center justify-between mb-2" data-id="element-2030">
                          <p className="text-xs font-medium text-gray-900" data-id="element-2031">
                            Media Library
                          </p>
                          <button type="button" onClick={() => {
                    setShowMediaPicker(false);
                    setMediaPickerSearch('');
                  }} className="text-gray-400 hover:text-gray-600" data-id="element-2032">
                            <XIcon className="h-3.5 w-3.5" data-id="element-2033" />
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-500 mb-2" data-id="element-2034">
                          Click an image to reference it in your prompt
                        </p>
                        {mediaImages.length > 4 && <input type="text" placeholder="Search images..." value={mediaPickerSearch} onChange={e => setMediaPickerSearch(e.target.value)} className="w-full px-2.5 py-1.5 border border-gray-200 rounded-md text-xs focus:ring-emerald-500 focus:border-emerald-500" autoFocus data-id="element-2035" />}
                      </div>
                      <div className="max-h-64 overflow-y-auto p-2" data-id="element-2036">
                        {filteredMediaImages.length === 0 ? <p className="text-xs text-gray-500 text-center py-4" data-id="element-2037">
                            {mediaPickerSearch ? 'No images match your search' : 'No images in library'}
                          </p> : <div className="grid grid-cols-3 gap-1.5" data-id="element-2038">
                            {filteredMediaImages.map(img => <button key={img.ID} type="button" onClick={() => handleInsertMediaImage(img)} className="group relative rounded-md overflow-hidden border border-gray-200 hover:border-emerald-400 hover:ring-2 hover:ring-emerald-100 transition-all text-left" title={`${img.Image_Name}${img.Description ? ` — ${img.Description}` : ''}`} data-id="element-2039">
                                <div className="aspect-square bg-gray-100" data-id="element-2040">
                                  {img.Image_URL ? <img src={img.Image_URL} alt={img.Image_Name} className="w-full h-full object-cover" onError={e => {
                        ;
                        (e.target as HTMLImageElement).style.display = 'none';
                      }} data-id="element-2041" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-2042">
                                      <ImageIcon className="h-5 w-5 text-gray-300" data-id="element-2043" />
                                    </div>}
                                </div>
                                <div className="px-1.5 py-1 bg-white" data-id="element-2044">
                                  <p className="text-[10px] font-medium text-gray-800 truncate" data-id="element-2045">
                                    {img.Image_Name}
                                  </p>
                                  {img.Category && <p className="text-[9px] text-gray-400 truncate" data-id="element-2046">
                                      {img.Category}
                                    </p>}
                                </div>
                                {/* Hover overlay */}
                                <div className="absolute inset-0 bg-emerald-600/0 group-hover:bg-emerald-600/10 transition-colors flex items-center justify-center" data-id="element-2047">
                                  <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-emerald-600 text-white text-[10px] font-medium px-2 py-0.5 rounded-full shadow-sm" data-id="element-2048">
                                    Use
                                  </span>
                                </div>
                              </button>)}
                          </div>}
                      </div>
                    </div>}
                </div>}
              {isLoadingMedia && <span className="inline-flex items-center gap-1 text-[11px] text-gray-400" data-id="element-2049">
                  <Loader2Icon className="h-3 w-3 animate-spin" data-id="element-2050" />
                  Loading media...
                </span>}
            </div>
          </div>
        </div>}
    </div>;
}